// Composition root: async bootstrap (PST-3), AppApi façade (ARC-3b), persistence hooks,
// two-tab watch (EC-ARC-7), and PWA registration (PST-5).
import { render } from "preact";
import { DocStore } from "./doc/store";
import type { Doc } from "./doc/entities";
import { Viewport, docBounds } from "./engine/viewport";
import { fitBounds } from "./engine/camera";
import { renderDocEntities } from "./engine/renderEntities";
import { ToolManager, type Prefs } from "./tools/toolManager";
import { openDriver } from "./io/idb";
import { bootstrapDoc, Persistence, type SaveStatus } from "./io/persist";
import { buildImportCommand, decodeImageFile, downscaleImage, type DecodedImage } from "./io/importImage";
import type { AppApi } from "./appApi";
import { App } from "./ui/app";
import { toasts } from "./ui/toasts";
import { takeLaunch, type LaunchInfo } from "./eden/launch";
import { deriveAll } from "./doc/derive";
import "./ui/theme.css";

// ARC-8: one global handler → console + toast. Committed work is autosaved.
window.addEventListener("error", (e) => {
  console.error("tracecad:", e.error ?? e.message);
  toasts.push("error", "Something went wrong — your work is saved.");
});
window.addEventListener("unhandledrejection", (e) => {
  console.error("tracecad:", e.reason);
});

/** PST-2 thumbnail (engine-side, injected into Persistence per ARC-3a). */
async function makeThumbnail(doc: Doc): Promise<Blob | null> {
  try {
    if (typeof OffscreenCanvas === "undefined") return null;
    const size = 256;
    const canvas = new OffscreenCanvas(size, size);
    const ctx = canvas.getContext("2d") as unknown as CanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.fillStyle = "#14161c";
    ctx.fillRect(0, 0, size, size);
    renderDocEntities({ ctx, cam: fitBounds(size, size, docBounds(doc)), cssW: size, cssH: size, dpr: 1 }, doc, new Set());
    return await canvas.convertToBlob({ type: "image/png" });
  } catch {
    return null; // cosmetic only — never block a save
  }
}

/** EC-ARC-7: warn when the same project is open in another tab. Last save wins. */
function armTabChannel(persist: Persistence): void {
  let channel: BroadcastChannel | null = null;
  let warned = false;
  const warn = () => {
    if (warned) return;
    warned = true;
    toasts.push("info", "This project is also open in another tab — last save wins.", true);
  };
  const setup = (projectId: string) => {
    warned = false;
    channel?.close();
    try {
      channel = new BroadcastChannel(`tracecad-${projectId}`);
    } catch {
      return; // BroadcastChannel unavailable → skip the warning feature
    }
    channel.onmessage = (e) => {
      if (e.data === "hello") {
        warn();
        channel?.postMessage("ack");
      } else if (e.data === "ack") {
        warn();
      }
    };
    channel.postMessage("hello");
  };
  persist.onProjectSwitch = setup;
  setup(persist.currentProjectId);
}

/** PST-5: hand-written service worker; update toast when a new version is waiting. */
function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker
    .register("/sw.js")
    .then((reg) => {
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing;
        sw?.addEventListener("statechange", () => {
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            toasts.push("info", "Update ready — reload the page to use the new version.", true);
          }
        });
      });
    })
    .catch((err) => console.warn("tracecad: SW registration failed", err));
}

/** Poll until `get()` returns non-null, or give up after `ms`. */
async function waitFor<T>(get: () => T | null, ms: number): Promise<T | null> {
  const deadline = Date.now() + ms;
  for (;;) {
    const v = get();
    if (v) return v;
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, 50));
  }
}

/**
 * E5 open-in-editor receiver. `alreadyLocal` = bootstrapDoc already opened it (nothing
 * to do but tidy the URL). Otherwise resolve the external_ref against Eden and adopt it
 * as a new local project. Always cleans the `?edenProject` param so a reload is inert.
 */
async function resolveEdenDeepLink(externalRef: string, needsPull: boolean, api: AppApi): Promise<void> {
  const clean = (): void => history.replaceState(null, "", location.pathname);
  if (!needsPull) {
    clean(); // bootstrapDoc opened the local project — done
    return;
  }
  try {
    const eden = await import("./eden");
    const cfg = await eden.loadEdenConfig();
    if (!cfg) {
      toasts.push("info", "That project isn't on this device, and Eden sync isn't set up here to fetch it.");
      return;
    }
    const remote = (await eden.listProjects(cfg)).find((p) => p.externalRef === externalRef);
    if (!remote) {
      toasts.push("error", "Couldn't find that project on the Eden server.");
      return;
    }
    const { doc, fileEdenId, manifestId, repaired, warnings } = await eden.fetchProjectDoc(cfg, remote.edenId, { manifestId: remote.manifestId, fileDescription: remote.fileDescription });
    const persist = await waitFor(() => api.getPersist(), 5000);
    if (!persist) return;
    await persist.adoptDoc(doc); // assigns a fresh local id
    eden.mapAdoptedProject(api.store.doc.id, remote, fileEdenId, manifestId); // …stays linked for re-sync; a save updates this manifest
    api.getViewport()?.zoomExtents();
    toasts.push("success", `Opened "${remote.name}" from Eden.`);
    for (const w of warnings) toasts.push("info", w, true);
    if (repaired?.length) toasts.push("info", `The drawing was repaired as it opened: ${repaired.join(" ")} Save it to keep the repair.`);
  } catch (e) {
    toasts.push("error", `Couldn't open the Eden-linked project: ${(e as Error).message}`);
  } finally {
    clean();
  }
}

/**
 * Eden asked for a NEW drawing on a job or tender (Eden 0.42.54). Start an empty
 * project named after it and hand the UI the host, fixed: the person gives it a
 * drawing number and draws. The first save links it and puts it on the record.
 */
async function startEdenDrawing(launch: LaunchInfo, api: AppApi): Promise<void> {
  const host = launch.host;
  if (!host) return;
  const refs = launch.refs ?? {};
  const persist = await waitFor(() => api.getPersist(), 5000);
  if (!persist) return;
  await persist.newProject(host.label);
  api.getViewport()?.zoomExtents();
  window.dispatchEvent(
    new CustomEvent("tracecad:eden-new", {
      detail: { label: host.label, jobId: refs.project_ref ?? "", tenderId: refs.tender_ref, refs }
    })
  );
}

async function boot(): Promise<void> {
  // First, before anything reads the address: take Eden's key out of it.
  const launch = takeLaunch();
  const driver = await openDriver();
  const savedPrefs = await driver.getSetting<Partial<Prefs>>("prefs");
  // E5 open-in-editor: Eden opens `?edenProject=<external_ref>`. external_ref is the
  // project's doc.id for project rows and "<doc.id>:doc" for file rows — strip a suffix.
  // A library object opened from Eden is not a drawing: say where it is used instead.
  const rawEdenProject = new URL(location.href).searchParams.get("edenProject") ?? "";
  if (rawEdenProject.startsWith("tracecad-lib:")) {
    history.replaceState(null, "", location.pathname);
    setTimeout(() => toasts.push("info", "That is a library object. Open the Library panel on the right and click it to place it."), 800);
  }
  const edenProject = rawEdenProject.startsWith("tracecad-lib:") ? null : rawEdenProject.split(":")[0] || null;
  const bs = await bootstrapDoc(driver, edenProject ?? undefined);
  const edenLinkNeedsPull = edenProject !== null && bs.projectId !== edenProject;
  const store = new DocStore(bs.doc);
  store.deriver = deriveAll; // pipes rebuild when a fitting or pipe end moves, walls when their spine or doors change (same undo step)
  if (bs.tail) store.restoreHistory(bs.tail);

  let viewport: Viewport | null = null;
  let tools: ToolManager | null = null;
  let persist: Persistence | null = null;

  const statusListeners = new Set<() => void>();
  let statusValue: SaveStatus = "idle";
  const setStatus = (s: SaveStatus) => {
    statusValue = s;
    for (const fn of statusListeners) fn();
  };

  const api: AppApi = {
    store,
    persistent: driver.persistent,
    getViewport: () => viewport,
    getTools: () => tools,
    getPersist: () => persist,
    saveStatus: {
      get: () => statusValue,
      subscribe: (fn) => {
        statusListeners.add(fn);
        return () => statusListeners.delete(fn);
      }
    },
    mountViewport: (container) => {
      viewport = new Viewport(container, store);
      tools = new ToolManager(viewport, store);
      viewport.delegate = { ...viewport.delegate!, spaceTap: () => tools!.repeatLastTool() };
      tools.persistPrefs = (p) => void driver.putSetting("prefs", p);
      tools.onNotice = (text) => toasts.push("info", text);
      if (savedPrefs) tools.applyPrefs(savedPrefs);
      persist = new Persistence(driver, store, {
        getViewState: () => ({
          camera: { ...viewport!.camera.current },
          activeLayerId: tools!.activeLayerId,
          guides: viewport!.guides.map((g) => ({ origin: { ...g.origin }, dir: { ...g.dir } }))
        }),
        applyViewState: (vs) => {
          viewport!.camera.current = { ...vs.camera };
          viewport!.guides = (vs.guides ?? []).map((g) => ({ origin: { ...g.origin }, dir: { ...g.dir } }));
          viewport!.scheduler.invalidateAll();
          tools!.setActiveLayer(vs.activeLayerId);
        },
        onStatus: setStatus,
        makeThumbnail
      });
      persist.currentProjectId = bs.projectId;
      persist.start();
      if (bs.repaired?.length) void persist.saveNow(); // keep the repair
      if (bs.viewState) {
        viewport.camera.current = { ...bs.viewState.camera };
        viewport.scheduler.invalidateAll();
        tools.setActiveLayer(bs.viewState.activeLayerId);
      }
      armTabChannel(persist);
      viewport.assets.loader = (hash) => driver.getAsset(hash).then((b) => b ?? null);
      return { viewport, tools, persist };
    },
    importFiles: async (all) => {
      if (!viewport || !tools) return;
      // DXF drawings are objects, not pictures: hand them to the DXF dialog (ui/dxfDialog).
      const dxf: File[] = [];
      const files: File[] = [];
      for (const f of all) (/\.(dxf|dwg)$/i.test(f.name) ? dxf : files).push(f);
      if (dxf.length) window.dispatchEvent(new CustomEvent("tracecad:dxf", { detail: dxf }));
      let cascade = 0;
      for (const file of files) {
        // EC-IMG-8: no imports while perspective mode is active
        if (tools.activeToolId === "perspective") {
          toasts.push("info", "Finish or cancel perspective first.");
          return;
        }
        let blob: Blob & { name?: string } = file;
        if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          const { renderPdfPage } = await import("./io/importPdf");
          // IMG-11: quick page picker via prompt (thumbnail dialog is M7 polish)
          const probe = await renderPdfPage(file, 1);
          if (!probe.ok) {
            toasts.push("error", probe.error);
            continue;
          }
          let pageBlob = probe.blob;
          if (probe.pageCount > 1) {
            const answer = prompt(`PDF has ${probe.pageCount} pages — which page?`, "1");
            const n = answer ? Number(answer) : 1;
            if (Number.isFinite(n) && n >= 2 && n <= probe.pageCount) {
              const again = await renderPdfPage(file, n);
              if (again.ok) pageBlob = again.blob;
            }
          }
          blob = pageBlob;
        }
        let outcome = await decodeImageFile(blob);
        if (!outcome.ok && "needsDownscaleConfirm" in outcome) {
          // EC-IMG-7 consent
          const yes = confirm(
            `Very large photo (${Math.round(outcome.megapixels)} MP) — downscale to 100 MP for smooth performance?`
          );
          if (yes) {
            const smaller = await downscaleImage(blob);
            outcome = smaller
              ? await decodeImageFile(smaller)
              : { ok: false, error: "Couldn't downscale the image." };
          } else {
            outcome = await decodeImageFile(blob, true);
          }
        }
        if (!outcome.ok) {
          toasts.push("error", "error" in outcome ? outcome.error : "Import failed.");
          continue; // EC-IMG-4: other files proceed
        }
        const image: DecodedImage = outcome.image;
        await driver.putAsset(image.hash, image.blob);
        const cam = viewport.camera.current;
        const { cmd, entityId } = buildImportCommand(
          store.doc,
          image,
          tools.activeLayerId,
          {
            centerWorld: { x: cam.cx, y: cam.cy },
            viewWmm: viewport.scheduler.width / cam.scale,
            viewHmm: viewport.scheduler.height / cam.scale
          },
          cascade++
        );
        await viewport.assets.set(image.hash, image.bitmap);
        store.dispatch(cmd);
        tools.selection.set([entityId]);
        tools.setHint?.("Image placed — press K to calibrate its real size, or right-click for perspective.");
      }
    }
  };

  render(<App api={api} />, document.getElementById("root")!);

  // RND-7: canvas fillText does NOT trigger lazy font loading — load all three families
  // explicitly, then repaint so canvas text isn't stuck on a fallback face.
  if (document.fonts?.load) {
    void Promise.allSettled([
      document.fonts.load("16px Inter"),
      document.fonts.load("bold 16px Inter"),
      document.fonts.load("16px Caveat"),
      document.fonts.load("16px 'JetBrains Mono'")
    ]).then(() => viewport?.scheduler.invalidateAll());
  }

  if (bs.loadError) {
    toasts.push("error", `The previous project couldn't be loaded: ${bs.loadError}`);
  }
  if (bs.repaired?.length) {
    toasts.push("info", `The drawing was repaired as it opened: ${bs.repaired.join(" ")} The repaired drawing is saved.`);
  }
  // E5: a deep-linked project not on this device → pull it from Eden (or clean up).
  if (edenProject) void resolveEdenDeepLink(edenProject, edenLinkNeedsPull, api);
  if (launch?.mode === "new") void startEdenDrawing(launch, api);
  registerServiceWorker();

  // Dev/E2E debugging handle only — never referenced by app code.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__tracecad = api;
  }
}

void boot();
